/*
 * Displays various geometry labels
 * Labels stored in feature properties or can be calculated in realtime
 * These types of calculated labels is supported:
 *      geometry segments length (in meters)
 *      geometry total length
 *      geometry square
 *      circle radius
 * Text output along a polyline is inspired by Leaflet.TextPath (https://github.com/makinacorpus/Leaflet.TextPath)
 *
 *    StarshipTroopers (C) 2021-2023
 */

L.GeometryDimension = {

    __onAddPolyline: L.Polyline.prototype.onAdd,
    __onRemovePolyline:  L.Polyline.prototype.onRemove,
    __onAddCircle: L.Circle.prototype.onAdd,
    __onRemoveCircle:  L.Circle.prototype.onRemove,
    __updatePathPolyline: L.Polyline.prototype._updatePath,
    __bringToFrontPolyline: L.Polyline.prototype.bringToFront,
    __bringToFrontCircle: L.Circle.prototype.bringToFront,
    __updatePathPolygon: L.Polygon.prototype._updatePath,
    __updatePathCircle: L.Circle.prototype._updatePath,

    _GD_defaultOptions: {
        attributes: {
            fill: "black",
            "font-weight": "bold",
        },
        display: {
            totalLength: true,
            segmentLength: true,
            area: true,
            label: true,
        },
        displayLabelCb: null, // if defined the displayLabelCb is called and must return the text to display
        fontSize: 20,         // in pixels by default
        cssClass: "lineLabelsDecor",
        textBaseLinePosition: "center",  // text Y position relative to the baseline, values: outward | inward | center
        textPosition: "center", // label position relative polygon bounds, values: center | top | bottom | topleft | topright | bottomleft | bottomright
        textPositionMarginY: 5, // bounds margin
        textPositionMarginX: 5,
        dy: 0,                // additional text Y position offset (in pixels) relative to the text baseline
        minIndent: 15,        // min indent from the edge of the segment or shape's line
        // Imperial System: ft, mi, Ac, Ha
        // Metric System: m, km, Ha
        unit: {
            imperial: false,
            acre: true,
            hectare: true,
        }
    },

    onAdd: function (map) {
        if (this instanceof L.Circle) {
            this.__onAddCircle.call(this, map);
        } else {
            this.__onAddPolyline.call(this, map);
        }
        this._textRecreate();
        this._zoomendHandler = function () {
            this._zoomChanged = true;
        }.bind(this);
        map.on("zoomend", this._zoomendHandler);

        // integration with geoman module
        this.on("pm:vertexadded pm:vertexremoved", this._textRedrawByEvent.bind(this))
        this.on("pm:markerdragend pm:dragend", this._textRecreate.bind(this))
        this.on("pm:enable pm:disable", this._textRecreate.bind(this))
    },

    onRemove: function (map) {
        map = map || this._map;
        if (this._debug) {
            console.log(`${L.Util.stamp(this)}: removeLayer`);
        }
        // integration with geoman module
        this.off("pm:vertexadded pm:vertexremoved", this._textRedrawByEvent.bind(this));
        this.off("pm:markerdragend pm:dragend", this._textRecreate.bind(this));
        this.off("pm:enable pm:disable", this._textRecreate.bind(this))
        map.off("zoomend", this._zoomendHandler);

        if (map && map._renderer._container)
            this._cleanupTextNodes();

        if (this instanceof L.Circle) {
            this.__onRemoveCircle.call(this, map);
        } else {
            this.__onRemovePolyline.call(this, map);
        }
    },

    bringToFront: function () {
        if (this instanceof L.Circle) {
            this.__bringToFrontCircle.call(this);
        } else {
            this.__bringToFrontPolyline.call(this);

        }

        this._updateTextNodes();
    },

    _updatePath: function () {
        if (this instanceof L.Polygon) {
            this.__updatePathPolygon.call(this);
        } else if (this instanceof L.Polyline) {
            this.__updatePathPolyline.call(this);
        } else if (this instanceof L.Circle) {
            this.__updatePathCircle.call(this);
        }
        if (this._GD_enabled && this._map.getBounds().intersects(this.getBounds())) {
            let t = "";

            if (this._debug) {
                if (this instanceof L.Polygon) {
                    t += "Polygon";
                } else if (this instanceof L.Polyline) {
                    t += "Polyline";
                } else if (this instanceof L.Circle) {
                    t += "Circle";
                }
                t = `${t} [${L.Util.stamp(this)}]: updatePath`;
                console.time(t);
            }

            if (this._zoomChanged) {
                this._textRecreate();
                this._zoomChanged = false;
            } else {
                this._textRedrawByEvent();
            }
            if (this._debug)
                console.timeEnd(t)
        } else {
            // cleanup nodes for path's out of the screen
            // todo possibile performance issue,
            // is this cleanup performing for all polygons on every screen update?
            this._cleanupTextNodes();
        }
    },

    _textRecreate: function () {
        // todo here ve can try to directly modified node text & position instead of recreate
        this._cleanupTextNodes()._updateTextNodes()
    },

    //layer is updated by some edit event like dragging or vertex moving
    _textRedrawByEvent: function (e) {
        //let t = new Date().getTime();
        //if ((this._GD_lastTextRedrawn == null) || ( t - this._GD_lastTextRedrawn > 100)) {
        this._updateTextNodes();
        //this._GD_lastTextRedrawn = t;
        //}
    },

    enableLabels: function () {
        if (this._GD_enabled)
            return
        this._GD_enabled = true;
        this._textRecreate();
    },

    disableLabels: function () {
        this._GD_enabled = false;
        this._cleanupTextNodes();
    },

    isLengthSegmentEnabled: function () {
        return this.options.GD.display.segmentLength
    },

    isLengthTotalEnabled: function () {
        return this.options.GD.display.totalLength
    },

    isAreaEnabled: function () {
        return this.options.GD.display.area
    },

    isLabelEnabled: function () {
        return this.options.GD.display.label
    },

    showLengthSegment: function () {this._showLabel('segmentLength');},

    hideLengthSegment: function () {this._hideLabel('segmentLength');},

    showLengthTotal: function () {this._showLabel('totalLength');},

    hideLengthTotal: function () {this._hideLabel('totalLength');},

    showArea: function () {this._showLabel('area');},

    hideArea: function () {this._hideLabel('area');},

    showLabel: function () {this._showLabel('label');},

    hideLabel: function () {this._hideLabel('label');},

    labelRefresh: function () {
        if (! this._GD_enabled)
            return
        this._textRecreate();
    },

    initDisplayOptions: function (displayOptions) {
        this.options.GD.display = displayOptions
    },

    /**
     * _showLabel(labelId) shows label with labelId
     * @private
     */
    _showLabel(labelId) {
        if (! this._GD_enabled)
            return
        this.options.GD.display[labelId] = true;
        this._textRecreate();
    },

    _hideLabel(labelId) {
        this.options.GD.display[labelId] = false;
        this._textRecreate();
    },

    _updateTextNodes() {
        if (!this._GD_enabled)
            return;

        let segments = this._calcSegments();
        let segmentsP = this._calcSegmentsP();

        //sometimes _path is undefined (perhaps at the moment when the layer is loaded but isn't initialized yet by leaflet)
        if (this._path == null) {
            return;
        }

        // bounding box with height and width in pixels, and x,y of top left corner on the screen
        let bounds = this._path.getBBox();

        //don't create nodes for small objects
        if (this instanceof  L.Polygon || this instanceof L.Circle) {
            if (bounds.width < 50 || bounds.height < 50) {
                this._cleanupTextNodes();
                return;
            }
        } else { //L.Polyline
            if (bounds.width < 50 && bounds.height < 50) {
                this._cleanupTextNodes();
                return;
            }
        }

        /*
        if (this._debug) {
            console.log(`${L.Util.stamp(this)}: LengthsP: ${JSON.stringify(segmentsP)}`);
            console.log(`${L.Util.stamp(this)}: LengthsM: ${JSON.stringify(segments)}`);
        }
         */

        //most likely the polygon is clipped by viewarea so path vertex is changed
        //console.log("Something get wrong with segments calculations");
        let wrongSegmentsCount = segmentsP.length !== segments.length

        //if segments count has been changed, recreate nodes
        if (!this._GD_segments || segments.length !== this._GD_segments.length) {
            this._cleanupTextNodes();
        }

        let svg = this._map._renderer._container;
        this._path.setAttribute('id', this._GD_pathdef);

        let options = this.options.GD;

        //define font-size according screen zoom
        let fontSize = options.fontSize;
        let zoom = this._map.getZoom();
        if (zoom > 20)
            fontSize = fontSize + ( zoom * 0.2).toFixed(0) * 1;

        //path line width?
        let lineWidth = this._path.getAttribute('stroke-width') * 1;

        // Define text Y position offset
        let dy = 0;
        if (options.textBaseLinePosition === "center") {
            dy = (fontSize / 4).toFixed(0) * 1 + lineWidth;
        } else if (options.textBaseLinePosition === "inward") {
            dy = fontSize - lineWidth - (fontSize / 8).toFixed(0);
        } else if (options.textBaseLinePosition === "outward") {
            dy = - (fontSize / 8).toFixed(0);
        }

        if (options.dy)
            dy += options.dy;

        if (!wrongSegmentsCount && options.display.segmentLength) {
            let offsetX = 0;
            for (let i in segmentsP) {
                let textNode = this._GD_segmentsTextNodes[i];
                if (!textNode) {
                    textNode = this._createTextNode(this._distanceHuman(segments[i].length), options, fontSize, true);
                    svg.appendChild(textNode);
                    this._GD_segmentsTextNodes[i] = textNode;
                } else {
                    // the length of a segment was changed
                    if (!this._GD_segments[i] || this._GD_segments[i].length !== segments[i].length)
                        this._updateTextNodeText(textNode, this._distanceHuman(segments[i].length))
                }

                let prevBearing = (this._GD_segments[i]) ? this._GD_segments[i].bearing : 0;
                //bearing has been changed, need to recreate textNode
                if ((prevBearing < 0 && segments[i].bearing >= 0) || (prevBearing >= 0 && segments[i].bearing < 0)) {
                    svg.removeChild(textNode);
                    textNode = this._createTextNode(this._distanceHuman(segments[i].length), options, fontSize, true);
                    svg.appendChild(textNode);
                    this._GD_segmentsTextNodes[i] = textNode;
                }

                // Define text X position
                let textLength = textNode.getComputedTextLength()
                let segmentOffset = this._blockInsertToCenterOffset(textLength, segmentsP[i].length, options.minIndent);
                //the text doesn't fit the segment
                if (segmentOffset == 0) {
                    svg.removeChild(textNode);
                    this._GD_segmentsTextNodes[i] = null;
                    offsetX += segmentsP[i].length;
                    continue;
                }

                // Set the position for the node according the position of polyline's segment
                textNode.setAttribute('dx', offsetX + segmentOffset);
                textNode.setAttribute('dy', dy);

                //rotate text to 180 if needed
                if (segments[i].bearing < 0) {
                    this._rotateTextNode(textNode, 180);
                }

                offsetX += segmentsP[i].length;
            }
        } else {
            this._cleanupSegmentsTextNodes();
        }

        let labelLines = []

        if (options.display.label && (this instanceof L.Polyline || this instanceof L.Circle)) {
            let labelText = "";
            if (typeof options.displayLabelCb === "function") {
                labelText = options.displayLabelCb(this);
            } else {
                if (typeof this.getProperty === "function") {
                    labelText = this.getProperty("descr") ?? "";
                }
            }
            if (labelText !== "") {
                let textNode = this._GD_labelTextNode;
                if (!textNode) {
                    textNode = this._createTextNode(labelText, options, fontSize, (this instanceof L.Polyline && !(this instanceof L.Polygon) && !(this instanceof L.Circle)));
                    svg.appendChild(textNode);
                    this._GD_labelTextNode = textNode;
                }

                if (this instanceof L.Polygon || this instanceof L.Circle) {
                    labelLines.push('_GD_labelTextNode')
                } else if (this instanceof L.Polyline) {
                    let textLength = textNode.getComputedTextLength();
                    let offset = this._blockInsertToCenterOffset(textLength, this._segmentsLength(segmentsP), options.minIndent);
                    if (offset === 0) {
                        this._cleanupLabelTextNode();
                    }
                    //trying to determine the segment we will place the label at
                    let i = this._getSegmentByOffset(segmentsP, offset);

                    //rotate text to 180 if needed
                    if (segments[i].bearing < 0) {
                        this._rotateTextNode(textNode, 180);
                    }
                    // Set the position for the node according the position of polyline's segment
                    textNode.setAttribute('dx', offset);
                    textNode.setAttribute('dy', dy);
                }
            } else {
                this._cleanupLabelTextNode();
            }
        } else {
            this._cleanupLabelTextNode();
        }

        if (options.display.area && (this instanceof L.Polygon || this instanceof L.Circle)) {

            let text = this._areaHuman(this.Area())
            if (this instanceof L.Circle) {
                text = "R " + this._distanceHuman(this._mRadius) + ", S " + text
            }
            let textNode = this._GD_areaTextNode;
            if (!textNode) {
                textNode = this._createTextNode(text, options, fontSize, false);
                svg.appendChild(textNode);
                this._GD_areaTextNode = textNode;
            }

            // polygon was changed, calculate Area
            //todo need to revise, something strange with unnecessary area calculation when polygon wasn't cahnged
            if (this._segmentsLength(this._GD_segments) !== this._segmentsLength(segments) ) {
                this._updateTextNodeText(textNode, this._areaHuman(this.Area()));
            }
            labelLines.push('_GD_areaTextNode')
        } else {
            this._cleanupAreaTextNode();
        }

        //set labels x,y position
        let finalLabelsToDisplay = []
        for (let i in labelLines) {
            let textNodeId = labelLines[i]
            let textNode = this[textNodeId]
            let textLength = textNode.getComputedTextLength();
            // text block doesn't fit to bounds
            if (this._blockInsertToCenterOffset(textLength, bounds.width, options.minIndent * 2) === 0) {
                this._cleanupTextNode(textNodeId)
                continue
            }
            // transform text coordinates according configured location
            let p = this._calculateLabelPosition(bounds, segmentsP, textLength, fontSize, labelLines.length, i)
            if (!p) {
                this._cleanupTextNode(textNodeId); // something got wrong
                continue
            } else {
                finalLabelsToDisplay.push({textNode: textNode, position: [bounds.x + p[0], bounds.y + p[1]]})
            }
        }

        for (let i in finalLabelsToDisplay) {
            finalLabelsToDisplay[i].textNode.setAttribute("x", finalLabelsToDisplay[i].position[0])
            finalLabelsToDisplay[i].textNode.setAttribute("y", finalLabelsToDisplay[i].position[1])
        }

        if (! wrongSegmentsCount && options.display.totalLength && (this instanceof L.Polyline) && ! (this instanceof L.Polygon)) {
            let textNode = this._GD_totalTextNode;
            let totalLength = this._segmentsLength(segments);
            if (!textNode) {
                textNode = this._createTextNode(this._distanceHuman(totalLength), options, fontSize, true);
                svg.appendChild(textNode);
                this._GD_totalTextNode = textNode;
            } else {
                if (this._segmentsLength(this._GD_segments) !== totalLength )
                    this._updateTextNodeText(textNode, this._distanceHuman(totalLength))
            }

            // Define text X position
            let textLength = textNode.getComputedTextLength()
            let offset = this._blockInsertToCenterOffset(textLength, this._segmentsLength(segmentsP), options.minIndent);
            //the text doesn't fit the line

            if (offset === 0) {
                this._cleanupTotalTextNode();
            } else {
                //trying to determine the segment we will place the label at
                let i = this._getSegmentByOffset(segmentsP, offset);

                /*

                let foundSegment = null;
                let foundOffset = 0;
                let totalSegments = segmentsP.length;
                let j = i * 1 + 1;
                while (foundSegment == null && (i >= 0 || j < totalSegments)) {
                    let offset = 0
                    if (i >= 0) {
                        offset = this._blockInsertToCenterOffset(textLength, segmentsP[i].length, options.minIndent)
                    }
                    if (offset > 0) {
                        foundOffset = offset
                        foundSegment = i
                        break
                    } else {
                        i--
                    }
                    if (j < totalSegments) {
                        offset = this._blockInsertToCenterOffset(textLength, segmentsP[j].length, options.minIndent)
                    }
                    if (offset > 0) {
                        foundOffset = offset
                        foundSegment = j
                        break
                    } else {
                        j++
                    }
                }

                if (foundSegment) {
                    console.log(`foundSegment ${foundSegment}`)
                    offset = 0
                    for (let i = 0; i < foundSegment; i++) {
                        offset += segmentsP[i].length;
                    }
                    offset += foundOffset;
                }
                 */

                // Set the position for the node according the position of polyline's segment
                textNode.setAttribute('dx', offset);
                textNode.setAttribute('dy', dy);

                //rotate text to 180 if needed
                if (segments[i].bearing < 0) {
                    this._rotateTextNode(textNode, 180);
                }
            }
        } else {
            this._cleanupTotalTextNode();
        }

        this._GD_segments = segments;
        this._GD_segmentsP = segmentsP;
    },

    /*
     *
     * https://www.geeksforgeeks.org/find-the-centroid-of-a-non-self-intersecting-closed-polygon/
     */
    _polygonCentroid(segments) {

        if (!segments || segments.length === 0) {
            console.warn('polygonCentroid: segments not passed')
            return null;
        }

        let centerX = 0;
        let centerY = 0;
        let signedArea = 0;

        let x0, x1, y0, y1, A, i;
        let n = segments.length;

        // For all vertices
        for (i = 0; i < n; i++)
        {
            x0 = segments[i].point.x;
            y0 = segments[i].point.y;
            x1 = segments[(i + 1) % n].point.x;
            y1 = segments[(i + 1) % n].point.y;

            // Calculate value of A
            // using shoelace formula
            A = (x0 * y1) - (x1 * y0);
            signedArea += A;

            // Calculating coordinates of
            // centroid of polygon
            centerX += (x0 + x1) * A;
            centerY += (y0 + y1) * A;
        }

        signedArea *= 0.5;
        centerX = centerX / (6 * signedArea);
        centerY = centerY / (6 * signedArea);

        return L.point(centerX, centerY)
            //[center[0].toFixed(0) * 1, center[1].toFixed(0) * 1];
    },

    // returns segment number at offset
    _getSegmentByOffset(segments, offset) {
        let l = 0;
        let i = 0;
        for (i in segments) {
            l += segments[i].length;
            if (l > offset) {
                break
            }
        }
        return i
    },

    // returns the offset or 0 if the block doesn't fit the length
    _blockInsertToCenterOffset(blockLength, totalLength, minIndent) {
        // Define text X position
        let offset = (totalLength / 2) - (blockLength / 2);
        //the text doesn't fit the segment
        if (offset - minIndent < 0)
            offset = 0

        return offset;
    },

    //cleanupTextNodes
    _cleanupTextNodes() {
        this._cleanupSegmentsTextNodes();
        this._cleanupAreaTextNode();
        this._cleanupLabelTextNode();
        this._cleanupTotalTextNode();
        this._GD_segments = [];
        this._GD_segmentsP = [];
        return this;
    },

    _cleanupSegmentsTextNodes() {
        if (this._GD_segmentsTextNodes && this._GD_segmentsTextNodes.length != 0) {
            for (let i in this._GD_segmentsTextNodes) {
                let node = this._GD_segmentsTextNodes[i];
                if (node && node.parentNode) {
                    this._map._renderer._container.removeChild(node);
                }
            }
            this._GD_segmentsTextNodes = [];
        }
    },

    _cleanupAreaTextNode() {
        this._cleanupTextNode('_GD_areaTextNode')
    },

    _cleanupLabelTextNode() {
        this._cleanupTextNode('_GD_labelTextNode')
    },

    _cleanupTotalTextNode() {
        this._cleanupTextNode('_GD_totalTextNode')
    },

    _cleanupTextNode(nodeId) {
        let node = this[nodeId]
        if (node && node.parentNode) {
            this._map._renderer._container.removeChild(node);
        }
        this[nodeId] = null;
    },

    _updateTextNodeText(textNode, text) {
        try {
            //textNode with inner textPath
            textNode.childNodes[0].removeChild(textNode.childNodes[0].childNodes[0]);
            textNode.childNodes[0].appendChild(document.createTextNode(text));
        } catch (e) {
            //textNode
            textNode.removeChild(textNode.childNodes[0]);
            textNode.appendChild(document.createTextNode(text));
        }
    },

    // creates svg textNode, bindToPath (boolean) value determines if the text should be located along the path element
    _createTextNode(text, options, fontSize, bindToPath) {
        let textNode = L.SVG.create('text');
        let textPath = null;
        if (bindToPath) {
            textPath = L.SVG.create('textPath');
            textPath.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", '#' + this._GD_pathdef);
        }

        textNode.setAttribute('dy', 0);
        textNode.setAttribute('class', options.cssClass);
        textNode.setAttribute("font-size", fontSize ? fontSize : options.fontSize + "px");

        for (let attr in options.attributes)
            textNode.setAttribute(attr, options.attributes[attr]);

        if (bindToPath) {
            textPath.appendChild(document.createTextNode(text));
            textNode.appendChild(textPath);
        } else {
            textNode.appendChild(document.createTextNode(text));
        }

        return textNode;
    },

    /**
     * calculates Label x,y position according position configuration and line number
     * @param bounds
     * @param segmentsP
     * @param textLength
     * @param fontSize
     * @param totalLines
     * @param lineNo
     * @returns {(number|*)[]}
     * @private
     */
    _calculateLabelPosition(bounds, segmentsP, textLength, fontSize, totalLines, lineNo) {
        let x,y = 0

        let p = this.options.GD.textPosition
        if (p === "center" || p === "top" || p === "bottom") {
            // calculate center
            if (this instanceof L.Polygon) {
                // polygon was changed, calculate centroid (position where label & area is located)
                if (this._segmentsLength(this._GD_segmentsP) !== this._segmentsLength(segmentsP) ) {
                    let center = this._polygonCentroid(segmentsP);
                    this._GD_polygonCentroidRelativeToBBox = [center.x - bounds.x, center.y - bounds.y]
                }
            }

            if (this instanceof L.Circle) {
                this._GD_polygonCentroidRelativeToBBox = [Math.round(bounds.width / 2), Math.round(bounds.height / 2)]
            }

            x = this._GD_polygonCentroidRelativeToBBox[0] - textLength / 2
            y = this._GD_polygonCentroidRelativeToBBox[1] - fontSize * totalLines / 2 + fontSize * lineNo + fontSize
        }

        if (p === "topright" || p === "bottomright") {
            x = bounds.width - textLength - this.options.GD.textPositionMarginX
        }
        if (p === "topleft" || p === "bottomleft") {
            x = this.options.GD.textPositionMarginX
        }

        if (p.startsWith("bottom")) {
            y = bounds.height - fontSize * totalLines + fontSize * lineNo + fontSize - this.options.GD.textPositionMarginY
        }
        if (p.startsWith("top")) {
            y = fontSize / 2 + fontSize * lineNo + this.options.GD.textPositionMarginY + fontSize / 4
        }

        return [x,y]
    },

    _rotateTextNode(textNode, angle) {
        let rotateCenterX = (textNode.getBBox().x + textNode.getBBox().width / 2);
        let rotateCenterY = (textNode.getBBox().y + textNode.getBBox().height / 2);
        textNode.setAttribute('transform', 'rotate(' + angle + ' ' + rotateCenterX + ' ' + rotateCenterY + ')');
    },

    //todo need to add interactive handlers
    _interractive() {
        /* Initialize mouse events for the additional nodes */
        if (this.options.interactive) {
            if (L.Browser.svg || !L.Browser.vml) {
                textPath.setAttribute('class', 'leaflet-interactive');
            }

            var events = ['click', 'dblclick', 'mousedown', 'mouseover',
                'mouseout', 'mousemove', 'contextmenu'];
            for (var i = 0; i < events.length; i++) {
                L.DomEvent.on(textNode, events[i], this.fire, this);
            }
        }
    },

    //calculate polyline segments length (in points)
    _calcSegmentsP() {
        let segments = [];
        try {
            //path-data-polyfill.js provides getPathData method
            let segList = this._path.getPathData();
            let curX, curY, length, seg, i;
            for (i = 0; i < segList.length; i += 1) {
                seg = segList[i];
                if ( seg.type === 'L' || seg.type === 'Z' ) {
                    segments.push()
                }
                if (seg.type === 'L') {
                    segments.push({
                        length: Math.sqrt(Math.pow(curX - seg.values[0], 2) + Math.pow(curY - seg.values[1], 2)),
                        point: L.point(curX, curY)
                    })
                }
                if (seg.type === 'Z') {
                    segments.push({
                        length: Math.sqrt(Math.pow(segList[0].values[0] - curX, 2) + Math.pow(segList[0].values[1] - curY, 2)),
                        point: L.point(curX, curY),
                    })
                }
                curX = seg.values[0];
                curY = seg.values[1];
            }
        } catch (e) {
            segments = [];
        }
        return segments;
    },

    //calculate polyline segments length (in meters)
    _calcSegments() {
        if (! this.getLatLngs)
            return []
        return this._calcLatLngsSegments(this.getLatLngs());
    },

    //calc length of latlng segments
    _calcLatLngsSegments: function (latLngs) {
        let segments = [];
        //passes the multidimensional array recursively
        if (Array.isArray(latLngs[0])) {
            for (let i = 0; i < latLngs.length; i++) {
                return this._calcLatLngsSegments(latLngs[i])
            }
        }

        let latLng = null;
        for (let i in latLngs) {
            if (latLng == null) {
                latLng = latLngs[i]
                continue
            }
            segments.push({
                length: latLng.distanceTo(latLngs[i]),
                bearing: this._getBearing(latLng, latLngs[i]),
                latLng: latLng
            });
            latLng = latLngs[i];
        }

        if (this instanceof L.Polygon) {
            segments.push({
                length: latLng.distanceTo(latLngs[0]),
                bearing: this._getBearing(latLng, latLngs[0]),
                latLng: latLng
            });
        }

        return segments;
    },

    // calculates the polygon or circle area in m
    Area: function (latLngs) {

        if (this instanceof L.Circle) {
            return Math.PI * this._mRadius * this._mRadius;
        }
        // Ported from the OpenLayers implementation. See https://github.com/openlayers/openlayers/blob/master/lib/OpenLayers/Geometry/LinearRing.js#L270
        if (! latLngs) {
            let deepArray = function (latLngs) {
                //passes the multidimensional array recursively
                if (Array.isArray(latLngs[0])) {
                    for (let i = 0; i < latLngs.length; i++) {
                        return deepArray(latLngs[i]);
                    }
                }
                return latLngs;
            }
            latLngs = deepArray(this.getLatLngs());
        }

        //
        let pointsCount = latLngs.length,
            area = 0.0,
            d2r = Math.PI / 180,
            p1, p2;

        if (pointsCount > 2) {
            for (let i = 0; i < pointsCount; i++) {
                p1 = latLngs[i];
                p2 = latLngs[(i + 1) % pointsCount];
                area += ((p2.lng - p1.lng) * d2r) *
                    (2 + Math.sin(p1.lat * d2r) + Math.sin(p2.lat * d2r));
            }
            area = area * L.Projection.SphericalMercator.R * L.Projection.SphericalMercator.R / 2.0;
        }

        return Math.abs(area);
    },

    _segmentsLength: function (segments) {
        if (segments == null || segments.length == 0)
            return 0
        let l = 0;
        for (let i in segments) {
            l += segments[i].length;
        }
        return Math.trunc(l);
    },

    _distanceHuman: function (distance) {
        if (this.options.GD.unit.imperial) {
            return this._distanceHumanImperial(distance)
        }
        return this._distanceHumanMetric(distance)
    },

    _distanceHumanMetric: function (distance) {
        let d;
        if (distance === 0)
            return "";
        if (distance < 1000) {
            if (distance < 10) {
                distance = distance.toFixed(1)
            } else {
                distance = distance.toFixed(0);
            }
            d = distance + '\u00A0m';
        } else {
            distance = this._floatNumberToHuman(distance / 1000);
            d = distance + '\u00A0km';
        }
        return d;
    },

    _distanceHumanImperial: function (area) {

    },

    _areaHuman: function (area) {
        if (this.options.GD.unit.imperial) {
            return this._areaHumanImperial(area)
        }
        return this._areaHumanMetric(area)
    },

    _areaHumanMetric: function (area) {
        if (area === 0)
            return "";

        if (this.options.GD.unit.acre) {
            if (area > 4046.86 * 10) {
                let a = this._floatNumberToHuman(area / 4046.86)
                if (a < 500)
                    return a + '\u00A0ac';
            }
        }

        if (this.options.GD.unit.hectare) {
            if (area > 10000 * 10) {
                area = this._floatNumberToHuman(area / 10000)
                return area + '\u00A0Ha';
            }
        }

        if (area < 2000000) {
            return area.toFixed(0) + '\u00A0m²';
        } else {
            area = this._floatNumberToHuman(area / 1000000);
            return area + '\u00A0km²';
        }

    },

    _floatNumberToHuman: function (number) {
        if (number < 10) {
            return number.toFixed(2);
        } else if (number < 100) {
            return number.toFixed(1);
        } else {
            return number.toFixed(0);
        }
    },

    _areaHumanImperial: function (area) {

    },

    /***
     * Returns the bearing in degrees clockwise from north (0 degrees)
     from the first L.LatLng to the second, at the first LatLng
     @param {L.LatLng} latlng1: origin point of the bearing
     @param {L.LatLng} latlng2: destination point of the bearing
     @returns {float} degrees clockwise from north.
     Source: https://github.com/3mapslab/Leaflet.streetlabels/blob/master/src/Leaflet.streetlabels.js
     */
    _getBearing: function (startCoords, stopCoords) {
        var rad = Math.PI / 180,
            lat1 = startCoords.lat * rad,
            lat2 = stopCoords.lat * rad,
            lon1 = startCoords.lng * rad,
            lon2 = stopCoords.lng * rad,
            y = Math.sin(lon2 - lon1) * Math.cos(lat2),
            x = Math.cos(lat1) * Math.sin(lat2) -
                Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
        var bearing = ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
        return bearing >= 180 ? bearing - 360 : bearing;
    },

    _init: function () {
        this._GD_pathdef = 'pathdef-' + L.Util.stamp(this);
        this._GD_segmentsTextNodes = [];
        this._GD_areaTextNode = null;
        this._GD_totalTextNode = null;
        this._GD_labelTextNode = null;
        this._GD_polygonCentroidRelativeToBBox = null;
        this._GD_segments = [];
        this._GD_segmentsP = [];
        let options = Object.assign({}, this._GD_defaultOptions)
        options.attributes = Object.assign({}, this._GD_defaultOptions.attributes)
        options.display = Object.assign({}, this._GD_defaultOptions.display)
        options.unit = Object.assign({}, this._GD_defaultOptions.unit)
        L.setOptions(this, {GD: options})
    }
}

L.Polyline.include(L.GeometryDimension);
L.Polygon.include(L.GeometryDimension);
L.Circle.include(L.GeometryDimension);

L.Polyline.addInitHook(function (t) {
    this._init();
})

L.Circle.addInitHook( function (t) {
    this._init();
})

//add svg filters and css Classes to implement some text decorations, like shadows etc
/*
L.SVG.addInitHook( function () {

    let defs = `
    <style>
        .defaultGD {
            stroke: white;
            stroke-width: 1px;
        }
    </style>
    <filter id="GDshadow" x="-10%" y="-10%" width="100%" height="100%">
        <feGaussianBlur result="shadow" stdDeviation="2 2"></feGaussianBlur>
        <feOffset dx="1" dy="1"></feOffset>
    </filter>
    `;

    this.once("add", function () {
        let defsElement = L.SVG.create("defs");
        defsElement.innerHTML = defs;
        this._container.appendChild(defsElement);
    })

})
 */

L.LayerGroup.include({
    enableLabels: function() {
        for (var layer in this._layers) {
            if (typeof this._layers[layer].enableLabels === 'function') {
                this._layers[layer].enableLabels();
            }
        }
        return this;
    },

    disableLabels: function() {
        for (var layer in this._layers) {
            if (typeof this._layers[layer].disableLabels === 'function') {
                this._layers[layer].disableLabels();
            }
        }
        return this;
    }

});

